import {
  AgendaItem,
  AgendaItemType,
  BareEventSpeaker,
  Booth,
  Broadcast,
  EventState,
  EventUser,
  MicrophoneControlModeEnum,
  SpeakerStageModeEnum,
  TrackItem
} from './types';
import { FilterBarStateKey } from '../filterBar';
import { TranstitionItem } from '../stage/types';

import type { AiSummaryStatusResponse } from '@goldcast/api/backend';

import { LOCAL_STORAGE_KEYS } from '@/constants';
import i18n from '@/i18n';
import { getValueFromLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { UserRole } from '@/services/user.service';
import { RootState } from '@/store';
import { scheduleUserFetch } from '@/store/schedulers';
import { sortAgendaItems } from '@/utils/commonAgendaUtils';
import { dateFormatter, isBefore, getDateFormatterInEventLocale } from '@/utils/dateTime';

const ONE_HOUR = 60 * 60 * 1000;

const getters = {
  currentEvent(state: EventState) {
    return state.event;
  },
  currentOrganization(state: EventState) {
    return state.organization;
  },
  getDiscussionGroup: (state: EventState) => (id: string) => {
    let group;
    if (state.routeDiscussionGroup && state.routeDiscussionGroup?.id === id) {
      group = state.routeDiscussionGroup;
    } else {
      group = state.discussionGroups.find(group => group.id === id);
    }
    return group;
  },
  getTotalParticipants: (state: EventState) => {
    return state.event.totalParticipants;
  },
  currentEventAgenda: ({ event }: EventState): AgendaItem[] => {
    const { broadcasts = [], items = [] } = event.agenda || {};
    const agendaItems = broadcasts.concat(items);
    return sortAgendaItems(agendaItems);
  },
  allOnDemandBroadcasts: ({ event }: EventState) => {
    return event.agenda.broadcasts.filter((broadcast: Broadcast) => broadcast.is_available_on_demand);
  },
  currentEventBooths(state: EventState) {
    return (state.event?.booths || []) as Booth[];
  },
  currentEventBooth(state: EventState, g: any, rootState: RootState) {
    const getters = g as EventGetters;
    let id = rootState.route.query.boothID;
    if (!id && (rootState.route.name === 'EventBooth' || rootState.route.name === 'EventBooth2')) {
      id = rootState.route.params?.boothID;
    }
    return getters.currentEventBooths.find((booth: Booth) => booth.id === id);
  },
  agendaItem: (state: EventState, getters: any) => (id: string, type: string) => {
    return (getters as EventGetters).currentEventAgenda.find(
      (item: AgendaItem) => item.id === id && item.type === type
    );
  },
  currentEventBroadcast:
    (state: EventState, getters: any) =>
    (id: string): Broadcast => {
      return getters.currentEventAgenda.find((broadcast: AgendaItem) => broadcast.id === id);
    },

  // this will be shown on the hover of agenda - it should be all other live agenda
  // except the one in which the user is and filterd if agenda is disabled - in restricted track
  currentlyOtherLiveSessions: ({ event }: EventState, getters: any, rootState: RootState) => {
    const filteredBroadcasts = getters.currentEventAgenda.filter((a: AgendaItem) => {
      return a.type == 'BROADCAST' && !a.disabled && a.is_live && a.id !== rootState.route.params?.id;
    });
    return filteredBroadcasts;
  },

  /**
    Filter the agenga based on
      1. items which are in current track and for all exclude restricted track items (a.disabled)
      2. live agenda items
      3. upcoming agenda items - 1 hour
      4. not the current agenda item - that will act as redirecting agenda item so remove from the upcmoming list 
   */
  currentlyLiveAndOneHourUpcomingSessions: (
    state: EventState,
    getters: any,
    rootState: RootState,
    rootGetters: any
  ) => {
    const currentAgendaItems = getters.agendaItemsForCurrentDateFilterSelection('StageScreenSaver');
    const currentRedirectableAgendaItem = rootGetters['event/stage/currentRedirectableAgendaItem'];
    const now = (state.event.stage?.heartBeatDate || new Date()).getTime();
    const items = currentAgendaItems.filter((a: AgendaItem) => {
      const agendaStartTime = Date.parse(a.start_time);
      const allowedEndTime = Date.parse(a.allowed_end_time);
      const showNotStartedAgendaItems = agendaStartTime < now && a.live_start_time === null && allowedEndTime > now;
      const isPastSession = a?.live_start_time && a?.live_end_time;
      return (
        !a.disabled &&
        (a.is_live || (agendaStartTime >= now && agendaStartTime < now + ONE_HOUR) || showNotStartedAgendaItems) &&
        a.id !== currentRedirectableAgendaItem?.id &&
        !isPastSession &&
        a.type !== 'BREAK'
      );
    });
    return sortAgendaItems(items);
  },
  broadcastsByDates:
    (state: EventState, getters: any, rootState: RootState, rootGetters: any) => (page: FilterBarStateKey) => {
      const searchTerm = rootState.filterBar.EventAgendaPage.searchTerm;
      const filterTags = rootState.filterBar.EventAgendaPage.filterTags;
      const agendaFavoritesFilter = rootState.filterBar.EventAgendaPage.agendaFavoritesFilter;
      const agendaFavorites = rootState.user.currentEventUser?.status?.agendaFavorites;
      const sortBy = rootState.filterBar.EventAgendaPage.sortBy;
      const locale = state.event.locale;
      const useEventLocaleForDateTimeFormatting = state.useEventLocaleForDateTimeFormatting;

      let broadcasts = getters.currentEventAgenda;

      if (agendaFavoritesFilter && agendaFavorites) {
        broadcasts = broadcasts.filter((broadcast: AgendaItem) => {
          const isFavorited = agendaFavorites?.find(
            agendaFavorite => agendaFavorite.broadcast === broadcast.id || agendaFavorite.agenda_item === broadcast.id
          );

          if (isFavorited) {
            return true;
          }

          return false;
        });
      }

      if (searchTerm || filterTags.length > 0) {
        broadcasts = broadcasts.filter((broadcast: Broadcast) => {
          const tags = (broadcast.tags || []).map(t => t.toLowerCase());
          const valid =
            !searchTerm ||
            [broadcast.title, ...tags].reduce(
              (v: boolean, target: string) => v || target.toLowerCase().includes(searchTerm.toLowerCase()),
              false
            );
          return valid && filterTags.every((tag: string) => tags.includes(tag));
        });
      }

      if (state.event.on_demand_mode === true) {
        broadcasts = broadcasts.filter(
          (broadcast: Broadcast) => broadcast.is_available_on_demand && broadcast.on_demand_asset
        );
      }

      broadcasts.sort((a: Broadcast, b: Broadcast) => {
        if (sortBy.value === 'open') {
          const aLive = !!a.is_live;
          const bLive = !!b.is_live;
          if (aLive !== bLive) {
            return aLive ? -1 : 1;
          }
        } else if (sortBy.value === 'past') {
          const aPast = !!a.live_end_time;
          const bPast = !!b.live_end_time;
          if (aPast !== bPast) {
            return aPast ? -1 : 1;
          }
        } else if (sortBy.value === 'upcoming') {
          const aStarted = !!a.live_start_time;
          const bStarted = !!b.live_start_time;
          if (aStarted !== bStarted) {
            return aStarted ? 1 : -1;
          }
        }

        return 0;
      });
      const agendaItemsForCurrentTrack = rootGetters[`filterBar/${page}`].trackFilter?.agenda_items;
      if (agendaItemsForCurrentTrack) {
        broadcasts = broadcasts.filter((broadcast: Broadcast) =>
          agendaItemsForCurrentTrack.includes(`${broadcast.id}`)
        );
      }

      return broadcasts.reduce((acc: any, broadcast: Broadcast) => {
        const date = useEventLocaleForDateTimeFormatting
          ? getDateFormatterInEventLocale(locale).format(new Date(broadcast.start_time))
          : dateFormatter.format(new Date(broadcast.start_time));
        if (acc[date]) {
          acc[date] = [...acc[date], broadcast];
        } else {
          acc[date] = [broadcast];
        }

        return acc;
      }, {});
    },
  broadcastsWithDates:
    (state: EventState, getters: any, rootState: RootState, rootGetters: any) => (page: FilterBarStateKey) => {
      let currentDate = '';
      const agendaFavoritesFilter = rootState.filterBar.EventAgendaPage.agendaFavoritesFilter;
      const agendaFavorites = rootState.user.currentEventUser?.status?.agendaFavorites;
      const agendaItemsForCurrentTrack = rootGetters[`filterBar/${page}`].trackFilter?.agenda_items;
      const locale = state.event.locale;
      const useEventLocaleForDateTimeFormatting = state.useEventLocaleForDateTimeFormatting;
      let items = getters.currentEventAgenda;

      if (agendaFavoritesFilter && agendaFavorites) {
        items = items.filter((broadcast: any) => {
          return agendaFavorites?.find(
            agendaFavorite => agendaFavorite.broadcast === broadcast.id || agendaFavorite.agenda_item === broadcast.id
          );
        });
      }

      if (state.event.on_demand_mode === true) {
        items = items.filter((item: Broadcast) => item.is_available_on_demand && item.on_demand_asset);
      }

      if (agendaItemsForCurrentTrack) {
        items = items.filter((item: Broadcast) => agendaItemsForCurrentTrack.includes(`${item.id}`));
      }

      return items.reduce((acc: AgendaItem[], item: AgendaItem) => {
        const date = useEventLocaleForDateTimeFormatting
          ? getDateFormatterInEventLocale(locale).format(new Date(item.start_time))
          : dateFormatter.format(new Date(item.start_time));
        if (date !== currentDate) {
          currentDate = date;
          acc.push({ kind: 'date', title: date } as AgendaItem);
        }

        acc.push(item);
        return acc;
      }, []);
    },
  agendaItemsForCurrentDateFilterSelection:
    (state: EventState, getters: any, rootState: RootState, rootGetters: any) => (page: FilterBarStateKey) => {
      const filters = rootGetters[`filterBar/${page}`];
      const broadcastsAgendaItemsAndDates =
        filters.dateFilter?.value === 'all'
          ? getters.broadcastsWithDates(page)
          : getters.broadcastsByDates(page)[filters.dateFilter?.value];

      return broadcastsAgendaItemsAndDates || [];
    },
  currentBroadcast(state: EventState, getters: any, rootState: RootState) {
    return state.event?.agenda?.broadcasts?.find(b => b.id === rootState.route.params?.id) || {};
  },
  isRecording(state: EventState, getters: any, rootState: RootState) {
    return state.event?.event_type === 'Recording';
  },
  /**
   * Animated decks in the Recording Studio need both flags. USE_NEW_SLIDE_ANIMATION still gates the
   * animation stack itself (deck loading, publishedStage, playback controls); USE_RS_ANIMATED_SLIDES
   * only opts the RS branch into it, so a live event's behaviour is unaffected either way.
   */
  isRSAnimatedSlidesEnabled(state: EventState, getters: any) {
    return getters.isRecording && state.useNewSlideAnimation && state.useRSAnimatedSlides;
  },
  currentBroadcastLayoutSettings(state: EventState, getters: any, rootState: RootState): Broadcast['layout_settings'] {
    const currentBroadcast = getters.currentBroadcast;
    return currentBroadcast?.layout_settings || {};
  },
  isLayoutTestMode(state: EventState): boolean {
    return state.testingLayoutSettings?.noOfSpeakers > 0 || !!state.testingLayoutSettings?.isScreenShareOn;
  },
  useNewLayouts(state: EventState, getters: any) {
    return (publishedStageLayout?: string) => {
      if (!state.useNewLayouts) {
        return false;
      }
      if (state.event?.event_type === 'Recording') {
        return false;
      }

      const updatedSettings = state.updatedLayoutSettings;
      const broadcastSettings = getters.currentBroadcastLayoutSettings;
      const effectiveSettings = Object.keys(updatedSettings || {}).length ? updatedSettings : broadcastSettings;

      if (getters.isLayoutTestMode || Object.keys(effectiveSettings || {}).length) {
        return true;
      }

      const selectedLayout = publishedStageLayout || getters.currentBroadcast?.screen_share_layout || 'FLOATING';
      const oldLayouts = ['FLOATING', 'UNDER_SCREEN', 'FLEX_16_9', 'FLEX', 'DEFAULT', 'NO_SPEAKER_VIDEOS'];
      return !oldLayouts.includes(selectedLayout);
    };
  },
  supportedCaptionsLanguageList(
    _,
    getters: any
  ): {
    label: string;
    value: string;
  }[] {
    return (getters.currentEvent?.supported_languages ?? []).map(lang => ({
      label: lang.native_name,
      value: lang.code
    }));
  },
  getCurrentRouteAgendaItem(state: EventState, getters: any, rootState: RootState) {
    if (rootState.route?.fullPath?.includes('/broadcast')) {
      const currentBroadcast: Broadcast = getters.currentEventBroadcast(rootState.route.params?.id);
      return currentBroadcast;
    }
    return null;
  },
  isSpeakerOfCurrentBroadcast:
    (state: EventState, getters: any, rootState: RootState, rootGetters: any) => (userID: string) => {
      return (
        rootState.route?.fullPath?.includes('/broadcast') &&
        getters.currentEventBroadcast(rootState.route.params.id)?.speakerIds.includes(userID)
      );
    },

  usersbyMmid(state: EventState) {
    const a = state.userStateInvalidated;
    return state.event.users;

    /*
    return Object.keys(event.users).reduce<Record<string, User>>((acc, id) => {
      const user = event.users[id];
      acc[user.mmid] = user;
      return acc;
    }, {});
    */
  },
  userById: (state: any, getters?: any) => (id: string) => state.event?.users[id],
  // loadedParticipants({ event }: EventState, { usersbyMmid }: any) {
  //   return (event.participants || [])
  //     .map((user: User) => usersbyMmid[user.mmid] || user)
  //     .filter((user: User) => !user.eventUser.blocked && user.eventUser?.entered);
  // },
  agendaDateOptions(state: EventState, getters: any, rootState: RootState) {
    const locale = state.event.locale;
    const useEventLocaleForDateTimeFormatting = state.useEventLocaleForDateTimeFormatting;
    const makeDateFormatter = useEventLocaleForDateTimeFormatting
      ? getDateFormatterInEventLocale(locale)
      : dateFormatter;

    let def = { index: -1, date: '' };
    let previousDate = '';
    let dates = [] as any;
    const endOfDay = new Date().setHours(23, 59, 59, 999);
    const today = makeDateFormatter.format(endOfDay);

    getters.currentEventAgenda.forEach((item: AgendaItem) => {
      const date = makeDateFormatter.format(new Date(item.start_time));

      if (previousDate !== date) {
        dates.push({ value: date, label: date });
        previousDate = date;
        if (isBefore(item.start_time, endOfDay)) {
          def = { date, index: def.index + 1 };
        }
      }
    });

    if (def.date !== today && dates.length > def.index + 1) {
      def.index += 1;
    }

    if (dates.length > 1) {
      dates = [
        {
          value: 'all',
          label: i18n.global.t('@@translate@@ All Dates')
        },
        ...dates
      ];
      def.index++;
    }

    return {
      dates,
      defaultIndex: state.event.on_demand_mode ? 0 : def.index
    };
  },
  resources: (state: EventState) => (content_type: string, object_id: string, exclude: boolean) => {
    return [
      ...(state.event.resources?.[`${content_type}-${object_id}`] || []),
      ...((!exclude && state.event.resources?.event) || [])
    ].map(resource => ({
      ...resource,
      typeText: resource.s3hosted ? 'DOC' : 'LINK',
      text: resource.name,
      url: resource.key_or_url,
      link: resource.key_or_url
    }));
  },
  currentPageResources(state: EventState, getters: any, rootState: RootState) {
    let content_type = '';
    if (['EventBooth', 'EventBooth2'].includes(rootState.route.name?.toString() || '')) {
      content_type = 'booth';
    } else if (rootState.route.name === 'DiscussionRoom') {
      content_type = 'discussiongroup';
    } else if (getters.currentBroadcast?.id) {
      content_type = 'broadcast';
    }

    const object_id = rootState.route?.params?.id || rootState.route?.params?.boothID;

    return getters.resources(content_type, object_id);
  },
  tracks({ event }: EventState, getters: any, rootState: RootState) {
    const eventTracks = event.tracks || [];
    if (eventTracks.length === 1) {
      return eventTracks;
    }
    return [
      {
        title: 'All',
        label: i18n.global.t('@@translate@@ All Tracks'),
        value: 'All',
        all: true,
        sort_priority: 0
      }
    ]?.concat([...eventTracks].sort((a: TrackItem, b: TrackItem) => (a.sort_priority > b.sort_priority ? 1 : -1)));
  },
  stageChatMMID(state: EventState, getters: any, rootState: RootState) {
    return getters.userTrack?.channel_mmid || rootState.mattermost?.publicChannels?.offtopic;
  },
  userTrack(state: EventState, getters: any, rootState: RootState) {
    const trackId = rootState.user.currentEventUser?.track;
    const track = findCurrentTrack(state.event.tracks, trackId);

    if (track) {
      return track;
    }

    if (state.event.tracks.length) {
      return state.event.tracks[0];
    }

    return {
      id: '',
      agenda_items: []
    };
  },
  isAgendaItemInUserTrack(state: EventState, getters: any) {
    return (itemId: string) => getters.userTrack?.agenda_items.includes(`${itemId}`);
  },
  userTrackAgenda(state: EventState, getters: any) {
    return getters.currentEventAgenda.filter(({ id }: AgendaItem) => getters.isAgendaItemInUserTrack(id));
  },
  liveEventUserCount(state: EventState) {
    const { broadcasts } = state.event.agenda;
    const { activeUserCountUsingMmid } = state.event;
    const { activeUserCount } = state.event;

    return broadcasts?.reduce(
      (
        acc: Array<{ label: string; value: string; showEllipsisTip: boolean }>,
        broadcast: {
          title: string;
          mmid?: string;
          id?: string;
          is_live: boolean;
        }
      ) => {
        const activeUserCountAttr = state.event.use_mm_sockets ? activeUserCountUsingMmid : activeUserCount;
        const id = state.event.use_mm_sockets ? broadcast?.mmid : broadcast?.id;

        if (broadcast.is_live && id) {
          let broadcastCount = activeUserCountAttr && activeUserCountAttr[id || ''];
          if (!broadcastCount && broadcastCount !== 0) {
            broadcastCount = ' ';
          }
          acc.push({
            label: `${broadcast.title}`,
            value: broadcastCount,
            showEllipsisTip: true
          });
        }
        return acc;
      },
      []
    );
  },
  // liveBroadcastsCount(state: EventState) {
  //   return state.event.agenda.broadcasts.map((it: any) => it.is_live && it.id).filter((it: any) => !!it).length;
  // },
  isModeratorOfCurrentBroadcast: (state: EventState, getters: any, rootState: RootState) => {
    const isCurrentPageABroadcast = rootState.route?.fullPath?.includes('/broadcast');
    if (isCurrentPageABroadcast) {
      const currentBroadcast = getters.currentEventBroadcast(rootState.route.params.id);
      return (currentBroadcast?.moderator || []).some((modId: string) => {
        return modId === rootState.user.currentEventUser?.id;
      });
    }
    return false;
  },
  isUserOrganizer: (state: EventState, getters: any) => (userId: string) => {
    return [1, 2].includes(getters.userById(userId)?.eventUser.event_role);
  },
  isBroadcastOrganizer: (state: EventState, getters: any, rootState: RootState, rootGetters: any) => {
    try {
      return rootGetters['isEventOrganizer'] || getters.isModeratorOfCurrentBroadcast;
    } catch (err) {
      loggerService.error('error in isBroadcastOrganizer', err);
      return false;
    }
  },
  isEventRolesEnabled: (state: EventState, getters: any, rootState: RootState) => {
    return state.organization?.is_event_roles_enabled || false;
  },
  isBroadcastSpeakerOrOrganizer: (state: EventState, getters: any, rootState: RootState, rootGetters: any) => {
    return (
      (getters.isBroadcastOrganizer && getters.isBroadcastProducerOrCoordinator) ||
      getters.isSpeakerOfCurrentBroadcast(rootState.user.currentEventUser?.id)
    );
  },
  isBroadcastProducerOrCoordinator: (state: EventState, getters: any, rootState: RootState, rootGetters: any) => {
    if (!getters.isEventRolesEnabled) {
      return true;
    }
    return (
      rootState.route?.fullPath?.includes('/broadcast') &&
      getters.currentBroadcast?.staffs?.includes(rootState.user.currentEventUser?.id)
    );
  },
  isSpeakerOnly(state: EventState, getters: any, rootState: RootState) {
    return rootState.user.currentEventUser?.eventUser?.event_role === 4;
  },
  isSpeaker(state: EventState, getters: any, rootState: RootState, rootGetters: any) {
    try {
      if (getters.isSpeakerOnly) {
        return true;
      }
      const uberSpeakers = rootState.event.event?.rawSpeakersFromUber ?? [];
      loggerService.info('Detected speaker from uberdata', uberSpeakers);
      // It could be an organizer who is also a speaker

      return uberSpeakers.some((rawSpeaker: { id: string }) => rawSpeaker.id === rootState.user.currentEventUser?.id);
    } catch (err) {
      loggerService.error('Problem when deciding if the user is a speaker: ', err);
      return false;
    }
  },
  isSponsor(state: EventState, getters: any, rootState: RootState) {
    const userEventRole = rootState.user?.currentEventUser?.event_role;

    if (userEventRole === UserRole.SPONSOR) {
      return true;
    }

    return false;
  },
  isOrganizerOrSpeaker: (state: EventState, getters: any, rootState: RootState) => {
    return getters.isBroadcastSpeakerOrOrganizer || getters.isSpeakerOnly;
  },
  // MM_API_CONSOLIDATION targets attendees only; organizers and speakers keep the original Mattermost flow
  isMmApiConsolidationActive(state: EventState, getters: any, rootState: RootState, rootGetters: any): boolean {
    return state.useMmApiConsolidation && !rootGetters.isEventOrganizer && !getters.isOrganizerOrSpeaker;
  },
  isCurrentRouteBroadcast(state: EventState, getters: any, rootState: RootState) {
    return rootState.route?.fullPath?.includes('/broadcast');
  },
  eventSummary({ event }: EventState, getters: any, rootState: RootState, rootGetters: any) {
    return {
      ...event,
      id: event.id,
      title: event.title,
      logo: event.logo_url,
      banner: event.banner_url,
      notifications: [],
      cta: {
        link: event.cta_link,
        title: event.cta_title || i18n.global.t('@@translate@@ Learn More')
      },
      show_banner_gradient: event.show_banner_gradient
    };
  },
  themedAuth({ event }: EventState) {
    return !!event?.theme_obj?.auth_theme;
  },
  allowLogin({ event }: EventState) {
    return !event?.theme_obj?.disallow_login;
  },
  isLocalSlidesRecordingEnabled(state: EventState, getters: any) {
    return getters['isRecording'];
  },
  preEventFlow({ event }: EventState, getters: any, rootState: RootState, rootGetters: any) {
    // Enable direct enter without pre event steps based on query param
    if (rootState.route?.query?.direct_enter == 'true') {
      return [];
    }
    const { show_onboarding, enable_speaker_tech_check, on_demand_mode, enable_tech_check } = event;

    const isPastEvent = new Date(event.end_time) <= new Date();
    const is_in_person = rootState.user?.currentEventUser?.eventUser?.is_in_person;
    const isAStaffMember = rootGetters['isStaffMember'];
    const isMobile = window.mobileCheck && window.mobileCheck();
    const isHybrid = getters['isHybrid'];
    const isNewEventEntryExperienceEnabled = getters['isNewEventEntryExperienceEnabled'];

    const isAttendeeTechCheckPreviouslyPassed = getValueFromLocalstore(
      `${rootState.route.params?.eventID}${LOCAL_STORAGE_KEYS.attendeeTechCheck}`,
      false
    );

    const show_speaker_checks =
      (getters.isBroadcastOrganizer || getters.isSpeaker || isAStaffMember) &&
      enable_speaker_tech_check &&
      !on_demand_mode &&
      !isPastEvent &&
      !isNewEventEntryExperienceEnabled;

    let show_attendee_checks =
      !(getters.isBroadcastOrganizer || getters.isSpeaker || isAStaffMember) &&
      enable_tech_check &&
      !on_demand_mode &&
      !isPastEvent &&
      !isAttendeeTechCheckPreviouslyPassed &&
      !isMobile &&
      !isNewEventEntryExperienceEnabled;

    if (isHybrid && is_in_person) {
      show_attendee_checks = false;
    }

    let shouldShowOnboarding = show_onboarding;

    if (event.event_type === 'Recording') {
      const { first_name, last_name } = rootState.user?.currentEventUser || {};
      if (!first_name || !last_name) {
        shouldShowOnboarding = true;
      } else {
        shouldShowOnboarding = false;
      }
    }

    return [
      'EventSplash',
      ...(show_speaker_checks && !isMobile ? ['SpeakerTechSetup'] : []),
      ...(show_attendee_checks ? ['AttendeeTechSetup'] : []),
      ...(shouldShowOnboarding && !isMobile ? ['EventOnBoarding'] : [])
    ];
  },
  showPoweredBy(state: EventState) {
    const pricingConfig = state.pricingConfig;
    if (Object.keys(pricingConfig).length > 0) {
      return pricingConfig?.goldcast_branding_enabled ?? true;
    }

    return state.organization && state.organization?.goldcast_branding_enabled;
  },
  allEventSpeakers(state: EventState) {
    // NOTE: DO NOT REMOVE
    // ts-disable-next-line
    const a = state.userStateInvalidated;
    const speakers: BareEventSpeaker[] = state?.event?.rawSpeakersFromUber || [];
    const eventSpeakers: EventUser[] = [];
    for (let i = 0; i < speakers.length; i++) {
      const speaker: BareEventSpeaker = speakers[i];
      if (!speaker.hidden) {
        if (!speaker?.id) {
          continue;
        }
        const eventUser = state.event.users[speaker?.id];
        if (!eventUser) {
          scheduleUserFetch(speaker.id);
          continue;
        }
        eventSpeakers.push(eventUser);
      }
    }
    return eventSpeakers;
  },
  responsive({ event }: EventState, getters: any, rootState: RootState) {
    const { responsive } = event;
    const routeEventId = rootState.route.params.eventID;
    return !routeEventId || routeEventId !== event.id || responsive;
  },
  isHybrid({ event }: EventState) {
    return event.is_hybrid;
  },
  hasGifsEnabled({ event }: EventState) {
    return event.gifs_enabled;
  },
  hasEmojisEnabled(state: EventState) {
    return !state.disableChatEmoji;
  },
  isWebinar({ event }: EventState) {
    return (event.event_type || '').toUpperCase() === 'WEBINAR' || event.event_type === 'Recording';
  },
  isNewEventEntryExperienceEnabled({ event, organization }: EventState) {
    if (event.event_type === 'Recording') {
      return true;
    }
    return organization?.show_new_entry_experience;
  },
  useFullHD({ event, use1080VideoQuality }: EventState) {
    return event.enable_full_hd && use1080VideoQuality;
  },
  useSkip480({ useSkip480 }: EventState): boolean {
    return !!useSkip480;
  },
  isPushSpeakerModeEnabled(state: EventState, getters: any): boolean {
    return state.event?.speaker_stage_mode === SpeakerStageModeEnum.PUSH_SPEAKER && !getters.isRecording;
  },
  isAskToUnmuteModeEnabled(state: EventState, getters: any): boolean {
    return state.event?.microphone_control_mode === MicrophoneControlModeEnum.ASK_TO_UNMUTE && !getters.isRecording;
  },
  isDirectUnmuteModeEnabled(state: EventState, getters: any): boolean {
    return state.event?.microphone_control_mode === MicrophoneControlModeEnum.DIRECT_UNMUTE && !getters.isRecording;
  },
  isFloatingEmojiReactionsEnabled(state: EventState): boolean {
    return (
      state.event?.floating_emoji_reactions_enabled && state.event?.event_type !== 'Recording' && state.useFloatingEmoji
    );
  },
  isCatchMeUpEnabled(state: EventState): boolean {
    return state.useCatchMeUp && !!state.event?.event_catch_me_up_enabled;
  },
  catchMeUpSummary(state: EventState): AiSummaryStatusResponse | null {
    return state.catchMeUpSummary;
  },
  hasRequestedFullSummary(state: EventState): boolean {
    return state.hasRequestedFullSummary;
  },
  catchMeUpRequestedTaskId(state: EventState): string | null {
    return state.catchMeUpRequestedTaskId;
  },
  hasSubmittedCatchMeUpFeedbackForCurrentTask(state: EventState, getters: any): boolean {
    return (
      !!getters.catchMeUpRequestedTaskId &&
      state.catchMeUpFeedbackSubmittedForTaskId === getters.catchMeUpRequestedTaskId
    );
  },
  getCventIdentity(state: EventState, _getters: any, rootState: RootState) {
    return {
      attendeeId: rootState.user?.currentEventUser?.eventUser?.props?.cventAttendeeId ?? undefined,
      eventId: state.event?.cvent_event_id ?? undefined,
      accountMappingId: state.cventAccountMappingId ?? undefined,
      sessionId: state.cventSessionId ?? undefined,
      // Cvent environment the event belongs to; published as the activity envelope
      // `env`. Not an identity key — it does not gate publishing.
      environment: state.event?.cvent_environment ?? undefined
    };
  }
};

export function getAgendaItemType({ kind }: AgendaItem) {
  if (kind) {
    const types: { [k: string]: AgendaItemType } = {
      break: 'BREAK',
      booth: 'BOOTH',
      booth2: 'BOOTH2',
      room: 'DISCUSSION_GROUP'
    };
    return types[kind];
  }

  return 'BROADCAST';
}

export function findCurrentTrack(eventTracks: TrackItem[], userTrackId: string | undefined) {
  // CHECK:  this will always be true even if eventTracks is [], so it ll return undefined rather than null
  if (eventTracks) {
    return (
      eventTracks.find(({ id, disabled }: TrackItem) => !disabled && id === userTrackId) ||
      eventTracks.find(({ disabled }: TrackItem) => !disabled) ||
      eventTracks[0]
    );
  }

  return null;
}

export function itemInTrack(track: TrackItem | null, item: TranstitionItem) {
  if (!track) {
    loggerService.warn('Track was undefined while checking if item is in track. ', item);
    return false;
  }
  if ((item.id || '').toString().includes('__inalltracks__')) {
    return true;
  }
  if (!track.agenda_items?.length) {
    loggerService.error('Track has no agenda_items during itemInTrack check', { trackId: track.id, itemId: item.id });
  }
  return track.agenda_items.map(id => (id || '').toString()).includes((item.id || '').toString());
}

export default getters;

type GetterTree = typeof getters;
type GetterFunc<k extends keyof GetterTree> = GetterTree[k];
export type EventGetters = {
  [k in keyof GetterTree]: ReturnType<GetterFunc<k>>;
};
